import { Injectable } from '@angular/core';
import { BxDisclaimContent } from '@buildxact/ui-components/bx-disclaim';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { NoticeModuleName, NoticeType } from '../../../core/components/bx-notices/notices.enum';
import { Notice } from '../../../core/components/bx-notices/notices.model';
import { NoticesService } from '../../../core/components/bx-notices/notices.service';
import { BxLogger } from '../../../core/services/bx-logger.service';
import { StoresService } from '../../../core/services/stores';
import { noticeTextItemsToTranslationObject } from '../../../utils/helper-functions/notice-text-item-convert';
import { EstimateActions, EstimateViewStoresService, QuoteRequestApiService } from '../../estimates/estimate-view/shell';
import { QuoteRequestsStores } from './domain';

@Injectable({
    providedIn: 'root',
})
export class QuoteRequestsNoticeService {
    constructor(
        private readonly noticesService: NoticesService,
        private readonly quoteRequestApiService: QuoteRequestApiService,
        private readonly estimateViewStoresService: EstimateViewStoresService,
        private readonly quoteRequestsStores: QuoteRequestsStores,
        private readonly storesService: StoresService,
        private readonly translateService: TranslateService,
        private readonly logger: BxLogger
    ) {
        this.watchNoticeChanges();
        this.loadQuoteRequestsNotices();
    }

    private loadQuoteRequestsNotices() {
        this.noticesService.getNotices(NoticeModuleName.Rfq).subscribe((result) => {
            this.quoteRequestsStores.quoteRequestsNotices = this.mapToQuoteRequestDisclaim(result.data?.noticesByModule);
        });
    }

    private mapToQuoteRequestDisclaim(notices: Notice[]): BxDisclaimContent[] {
        return notices.map((notice) => {
            // @ts-ignore TS2345
            const translateParams = noticeTextItemsToTranslationObject(notice.textItems);

            const title =
                notice.type !== NoticeType.RfqExtendingDeadlineRequired
                    ? this.translateService.instant(`notices.${notice.type}.heading`, translateParams)
                    : translateParams['OBJECT_DESCRIPTION'];

            const content =
                notice.type !== NoticeType.RfqExtendingDeadlineRequired
                    ? this.translateService.instant(`notices.${notice.type}.body`, translateParams)
                    : translateParams['MESSAGE_SENDER_USERNAME'];

            return {
                hasIcon: true,
                title: title,
                severity: notice.severity,
                htmlContent: content,
                actions:
                    notice.type === NoticeType.RfqExtendingDeadlineRequired
                        ? [EstimateActions.ExtendDeadline, EstimateActions.Dismiss]
                        : [EstimateActions.View, EstimateActions.Dismiss],
                data: { quoteRequestId: notice.objectId, noticeId: notice.noticeId },
            };
        });
    }

    public updateQuoteRequestRecipient(action: EstimateActions, quoteRequestId: string, noticeId: string) {
        switch (action) {
            case EstimateActions.Dismiss:
            case EstimateActions.View:
                this.noticesService
                    .deleteNotice(noticeId)
                    .pipe(take(1))
                    .subscribe(() => this.loadQuoteRequestsNotices());
                break;

            case EstimateActions.ExtendDeadline:
                this.quoteRequestApiService.unacceptQuoteRequest(quoteRequestId).subscribe(() => {
                    this.logger.success('Deadline extended for 1 more week.');
                });
                break;
        }
    }

    public dismissAllQuoteRequestNotices() {
        const notices = this.quoteRequestsStores.quoteRequestsNotices;
        const objectIds = [...new Set(notices.map((notice) => (notice.data as { quoteRequestId: string }).quoteRequestId))];
        forkJoin(objectIds.map((objectId) => this.noticesService.deleteNoticesForObject(objectId))).subscribe(() => {
            this.loadQuoteRequestsNotices();
        });
    }

    private watchNoticeChanges() {
        this.storesService.refreshNotice$.subscribe(() => {
            this.loadQuoteRequestsNotices();
            this.estimateViewStoresService.refreshRfqStatus();
        });
    }
}
